import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const TIER_INFO = {
  starter: {
    name: 'Starter',
    setup: '$749',
    monthly: '$79/mo',
    features: 'Custom website, professional email, domain, SEO, monthly maintenance',
  },
  professional: {
    name: 'Professional',
    setup: '$1,250',
    monthly: '$199/mo',
    features: 'Everything in Starter + AI chatbot, review management, booking, analytics dashboard',
  },
  premium: {
    name: 'Premium',
    setup: '$1,997',
    monthly: '$399/mo',
    features: 'Everything in Professional + AI phone agent, email drips, full SEO, social media',
  },
  enterprise: {
    name: 'Enterprise',
    setup: 'Custom',
    monthly: 'Custom',
    features: 'Full AI business operating system, custom agents per department, dedicated infrastructure',
  },
};

const BUSINESS_TYPES = [
  'Restaurant',
  'Salon/Spa',
  'Contractor/Trades',
  'Retail',
  'Professional Services',
  'Auto/Mechanic',
  'Food/Beverage',
  'Health/Wellness',
  'Other',
];

const ContactUs = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tierParam = (queryParams.get('tier') || '').toLowerCase();
  const initialTier = TIER_INFO[tierParam] ? tierParam : '';
  const tierInfo = initialTier ? TIER_INFO[initialTier] : null;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    businessType: '',
    tierInterest: initialTier,
    subject: tierInfo ? `Consultation — ${tierInfo.name} Plan` : '',
    message: '',
  });

  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (initialTier && tierInfo) {
      setFormData(prev => ({
        ...prev,
        tierInterest: initialTier,
        subject: `Consultation — ${tierInfo.name} Plan`,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTier]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'contactRequests'), {
        ...formData,
        status: 'new',
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error submitting contact form:', err);
      alert('Sorry — something went wrong sending your message. Please email us directly at info@the-apn.com.');
      return;
    }
    setFormSubmitted(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      businessName: '',
      businessType: '',
      tierInterest: '',
      subject: '',
      message: '',
    });
  };

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Contact Us</h1>
          </div>
        </div>
      </div>

      {/* Tier Interest Banner */}
      {tierInfo && (
        <div className="bg-apn-blue text-white py-6 border-t border-apn-light-blue">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-apn-dark-blue rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-sm uppercase tracking-wide text-apn-light-blue font-semibold mb-1">
                    You're interested in our
                  </p>
                  <h2 className="text-2xl font-bold">
                    {tierInfo.name} Plan — {tierInfo.setup} setup + {tierInfo.monthly}
                  </h2>
                  <p className="text-apn-light-blue mt-2 text-sm">{tierInfo.features}</p>
                </div>
                <Link
                  to="/about/additional-services"
                  className="inline-flex items-center px-4 py-2 bg-white text-apn-blue font-medium rounded-lg hover:bg-gray-100 transition-colors text-sm whitespace-nowrap"
                >
                  Compare Plans →
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>
              <p className="text-lg text-gray-600 mb-8">
                Have questions about APN membership, sponsorship, or events? We're here to help! Fill out the form or use the contact information below to get in touch with our team.
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">General Inquiries</h3>
                  <p className="text-gray-600">
                    <strong>Email:</strong> info@the-apn.com
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Our Team</h3>
                  <div className="space-y-3">
                    <div className="text-gray-600">
                      <strong>Caroline Burnham</strong> — Founder & CEO<br />
                      <a href="mailto:cburnham@the-apn.com" className="text-apn-blue hover:underline">cburnham@the-apn.com</a><br />
                      (724) 831-6105
                    </div>
                    <div className="text-gray-600">
                      <strong>Nicholas Agouras</strong> — CTO & Head of AI & Software Development<br />
                      <a href="mailto:nagouras@the-apn.com" className="text-apn-blue hover:underline">nagouras@the-apn.com</a><br />
                      (203) 460-2492
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Connect With Us</h3>
                  <div className="flex space-x-4">
                    <a
                      href="https://www.linkedin.com/company/accelerated-professionals-network/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-apn-blue transition-colors"
                    >
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                    </a>
                    <a
                      href="https://www.instagram.com/the_apn_/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-apn-blue transition-colors"
                    >
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>

              {formSubmitted ? (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
                  <p>Thank you for your message! We'll get back to you as soon as possible.</p>
                </div>
              ) : null}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    />
                  </div>
                  <div>
                    <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      id="businessName"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(203) 555-1234"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-1">
                      Business Type
                    </label>
                    <select
                      id="businessType"
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue bg-white"
                    >
                      <option value="">Select your industry...</option>
                      {BUSINESS_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="tierInterest" className="block text-sm font-medium text-gray-700 mb-1">
                      Tier Interest
                    </label>
                    <select
                      id="tierInterest"
                      name="tierInterest"
                      value={formData.tierInterest}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue bg-white"
                    >
                      <option value="">Not sure yet</option>
                      <option value="starter">Starter — $749 + $79/mo</option>
                      <option value="professional">Professional — $1,250 + $199/mo</option>
                      <option value="premium">Premium — $1,997 + $399/mo</option>
                      <option value="enterprise">Enterprise — Custom pricing</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-apn-blue text-white py-3 px-4 rounded-md hover:bg-apn-dark-blue transition duration-200 font-semibold"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Ready to Join the Network?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Connect with finance professionals, access exclusive resources, and accelerate your career with APN.
          </p>
          <Link to="/register" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Become a Member Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
